import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { LangContext } from "../../contexts/LangContext";
import en from "../../i18n/en";
import pl from "../../i18n/pl";
import { FaInstagram, FaFacebook, FaLinkedin, FaServer } from "react-icons/fa";
import { HiInformationCircle, HiLightBulb, HiExternalLink, HiRefresh, HiUpload, HiTrash } from "react-icons/hi";
import ISO6391 from "iso-639-1";
import FilteringSection from "./FilteringSection";
import { getCookie, getCookieJSON } from "../../services/cookie";
import "../../styles/CreateTask.css";

const STORAGE_KEY = "create_scraping_data";
const API_BASE = "https://flautreach.com/flautreach/api";

// ==================== Socials ====================
function getSocials(t, isAdmin) {
  const s = t.createTaskPage.scraping;

  const instagramSubtypes = [
    { id: "instagram_posts", name: s.posts, description: s.findInstagramPosts },
    { id: "instagram_pages", name: s.pages, description: s.findInstagramProfiles, hasAuthorOption: true },
    { id: "instagram_reels", name: s.reels, description: s.findInstagramReels },
  ];
  
  if (isAdmin) {
    instagramSubtypes.push({
      id: "instagram_adb",
      name: "ADB Processing",
      description: s.adbSubtypeDesc || "Process existing Instagram Profiles via ADB",
    });
    instagramSubtypes.push({
      id: "instagram_adb_spider",
      name: s.adbSpiderLabel || "ADB Spider",
      description: s.adbSpiderDesc || "Spider using ADB with keywords",
    });
  }

  const facebookSubtypes = [
    { id: "facebook_posts", name: s.posts, description: s.findFacebookPosts },
    { id: "facebook_pages", name: s.pages, description: s.findFacebookPages },
  ];
  
  if (isAdmin) {
    facebookSubtypes.push({
      id: "facebook_screenshot",
      name: s.facebookScreenshotLabel || "Facebook Page Screenshot",
      description: s.facebookScreenshotDesc || "Process existing Facebook tasks via Screenshot",
    });
  }

  const socials = [
    {
      id: "instagram",
      name: "Instagram",
      icon: <FaInstagram size={24} color="#E4405F" />,
      color: "#E4405F",
      subtypes: instagramSubtypes,
    },
    {
      id: "facebook",
      name: "Facebook",
      icon: <FaFacebook size={24} color="#1877F2" />,
      color: "#1877F2",
      subtypes: facebookSubtypes,
    },
    {
      id: "linkedin",
      name: "LinkedIn",
      icon: <FaLinkedin size={24} color="#0A66C2" />,
      color: "#0A66C2",
      subtypes: [
        { id: "linkedin_posts", name: s.posts, description: s.findLinkedInPosts },
      ]
    },
  ];

  // Добавляем DNS как отдельную платформу только для админов
  if (isAdmin) {
    socials.push({
      id: "dns",
      name: "DNS",
      icon: <FaServer size={24} color="#6C5CE7" />,
      color: "#6C5CE7",
      subtypes: [
        { 
          id: "dns_filtering", 
          name: "DNS Filtering", 
          description: "Upload txt files and filter by keyword conditions" 
        },
      ],
    });
  }

  return socials;
}

const PLATFORM_SITES = {
  instagram_posts: "site:instagram.com",
  instagram_pages: "site:instagram.com",
  instagram_reels: "site:instagram.com/reel",
  facebook_posts: "site:facebook.com",
  facebook_pages: "site:facebook.com",
  linkedin_posts: "site:linkedin.com",
};

function getPeriodOptions(t) {
  const s = t.createTaskPage.scraping;
  return [
    { value: "any", label: s.anyTime },
    { value: "y", label: s.pastYear },
    { value: "m", label: s.pastMonth },
    { value: "w", label: s.pastWeek },
    { value: "d", label: s.past24h },
    { value: "h", label: s.pastHour },
  ];
}

const POPULAR_LANGUAGES = ["en", "pl", "de", "fr", "es", "it", "ro", "sr", "ru", "ua", "pt", "nl", "se", "no", "cz"];

const getDefaultState = () => ({
  keywords: "",
  selectedSocial: "",
  selectedPlatform: "",
  whoToFind: "",
  prevTarget: "",
  aiPrompt: "",
  promptTemplate: "",
  isManuallyEdited: false,
  showAdvanced: false,
  advancedSettings: { searchLanguage: "en", scrapingPeriods: ["any"], maxPages: 5 },
  scheduleType: "once",
  repeatInterval: "daily",
  repeatCount: 1,
  parseAuthorPages: true,
  // ADB
  adbAfterScraping: false,
  adbTargetTaskId: "",
  availableAdbTasks: [],
  adbTasksLoading: false,
  adbSourceType: "task", // "task" | "urls"
  adbDirectUrls: "",
  // Facebook Screenshot
  screenshotAfterScraping: false,
  screenshotTargetTaskId: "",
  availableScreenshotTasks: [],
  screenshotTasksLoading: false,
  // ADB Spider
  spiderSourceTaskId: "",
  spiderSourceTasks: [],
  spiderTasksLoading: false,
  // DNS
  dnsFiles: [],
  dnsConditions: "",
});

// ==================== HelpTooltip ====================
function HelpTooltip({ content, children }) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const { lang } = useContext(LangContext);
  const t = lang === "pl" ? pl : en;

  useEffect(() => {
    if (!show) return;
    const handleOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    const handleEsc = (e) => { if (e.key === "Escape") setShow(false); };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [show]);

  if (!content) {
    return children ? children : <HiInformationCircle className="help-tooltip-icon-muted" />;
  }

  return (
    <span ref={ref} className="help-tooltip-wrapper">
      <span
        className="help-tooltip-trigger"
        onClick={(e) => { e.stopPropagation(); setShow(!show); }}
        title={t.createTaskPage.scraping.clickForHelp}
      >
        {children ? children : <HiInformationCircle className="help-tooltip-icon" />}
      </span>
      {show && (
        <>
          <div className="help-tooltip-popup-backdrop" onClick={() => setShow(false)} />
          <div className="help-tooltip-popup">
            <div className="help-tooltip-header">
              <HiLightBulb className="help-tooltip-header-icon" />
              <span className="help-tooltip-header-title">{content.title}</span>
            </div>
            <div className="help-tooltip-tips">
              {content.tips.map((tip, idx) => (
                <div key={idx} className="help-tooltip-tip">
                  <span className="help-tooltip-tip-bullet">•</span>
                  <span>{tip}</span>
                </div>
              ))}
            </div>
            {content.links && content.links.length > 0 && (
              <div className="help-tooltip-links">
                {content.links.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="help-tooltip-link"
                  >
                    <HiExternalLink size={12} />{link.text}
                  </a>
                ))}
              </div>
            )}
            <div className="help-tooltip-close-hint">{t.createTaskPage.scraping.clickOutsideToClose}</div>
          </div>
        </>
      )}
    </span>
  );
}

// ==================== DNS Condition Parser ====================
function parseDnsCondition(line) {
  if (!line.trim()) return null;
  
  // Check if line contains multiple keywords (comma or && separated)
  const parts = line.split(/[&,]+/).map(p => p.trim()).filter(p => p.length > 0);
  
  if (parts.length > 1) {
    // Multiple keywords - ALL must match
    const keywords = parts.map(part => {
      const caseSensitive = part.startsWith("cs:");
      const keyword = caseSensitive ? part.substring(3) : part;
      return { keyword, caseSensitive };
    });
    return { type: "and", keywords };
  }
  
  // Single keyword
  const part = parts[0];
  const caseSensitive = part.startsWith("cs:");
  const keyword = caseSensitive ? part.substring(3) : part;
  
  if (keyword.startsWith("!")) {
    // Negative condition
    return { type: "not", keyword: keyword.substring(1), caseSensitive };
  }
  
  return { type: "single", keyword, caseSensitive };
}

// ==================== Main Component ====================
export default function ScrapingForm({
  IS_ADMIN,
  onSave,
  onCancel,
  taskName,
  includeFiltering = false,
  mode = "standalone",
  onStateChange,
  prefillData = null,
}) {
  const { lang } = useContext(LangContext);
  const t = lang === "pl" ? pl : en;
  const s = t.createTaskPage.scraping;
  const helpContent = s.help;

  const SOCIALS = getSocials(t, IS_ADMIN);
  const PERIOD_OPTIONS = getPeriodOptions(t);
  const userInfo = getCookieJSON("userInfo");

  const getDefaultTemplate = useCallback(
    (platform) => {
      if (!platform) return s.defaultPrompt ? s.defaultPrompt : "";
      const prompts = s.defaultPrompts ? s.defaultPrompts : {};
      return prompts[platform] ? prompts[platform] : (s.defaultPrompt ? s.defaultPrompt : "");
    },
    [s.defaultPrompt, s.defaultPrompts]
  );

  const buildPrompt = useCallback((template, target) => {
    const tgt = target.trim() ? target.trim() : "{target}";
    return template.split("{target}").join(tgt);
  }, []);

  const getUniqueUrls = useCallback((urlsString) => {
    if (!urlsString || typeof urlsString !== 'string') return [];
    
    const urls = urlsString
      .split("\n")
      .map(url => url.trim())
      .filter(url => url.length > 0);
    
    const uniqueUrls = [];
    const seen = new Set();
    
    urls.forEach(url => {
      let normalized = url.toLowerCase();
      normalized = normalized.replace(/^@/, "");
      normalized = normalized.replace(/\/+$/, "");
      normalized = normalized.replace(/^https?:\/\//, "");
      normalized = normalized.replace(/^www\./, "");
      
      if (!seen.has(normalized)) {
        seen.add(normalized);
        uniqueUrls.push(url);
      }
    });
    
    return uniqueUrls;
  }, []);

  const getInitialState = () => {
    if (prefillData) {
      const merged = { ...getDefaultState(), ...prefillData };
      if (!merged.advancedSettings || !merged.advancedSettings.scrapingPeriods) {
        merged.advancedSettings = {
          ...merged.advancedSettings,
          scrapingPeriods: ["any"],
        };
      }
      if (merged.parseAuthorPages === undefined) merged.parseAuthorPages = true;
      if (!merged.promptTemplate && merged.aiPrompt) {
        merged.promptTemplate = merged.aiPrompt;
      }
      if (merged.adbAfterScraping === undefined) merged.adbAfterScraping = false;
      if (merged.adbTargetTaskId === undefined) merged.adbTargetTaskId = "";
      if (merged.adbSourceType === undefined) merged.adbSourceType = "task";
      if (merged.adbDirectUrls === undefined || merged.adbDirectUrls === null) merged.adbDirectUrls = "";
      if (merged.screenshotAfterScraping === undefined) merged.screenshotAfterScraping = false;
      if (merged.screenshotTargetTaskId === undefined) merged.screenshotTargetTaskId = "";
      if (merged.spiderSourceTaskId === undefined) merged.spiderSourceTaskId = "";
      if (merged.dnsFiles === undefined) merged.dnsFiles = [];
      if (merged.dnsConditions === undefined || merged.dnsConditions === null) merged.dnsConditions = "";
      if (merged.aiPrompt && !merged.isManuallyEdited) {
        merged._prefillPrompt = merged.aiPrompt;
        merged.promptTemplate = merged.aiPrompt;
      }
      return merged;
    }
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const merged = { ...getDefaultState(), ...parsed };
        if (!merged.advancedSettings || !merged.advancedSettings.scrapingPeriods) {
          merged.advancedSettings = {
            ...merged.advancedSettings,
            scrapingPeriods: ["any"],
          };
        }
        if (merged.parseAuthorPages === undefined) merged.parseAuthorPages = true;
        if (merged.adbAfterScraping === undefined) merged.adbAfterScraping = false;
        if (merged.adbTargetTaskId === undefined) merged.adbTargetTaskId = "";
        if (merged.adbSourceType === undefined) merged.adbSourceType = "task";
        if (merged.adbDirectUrls === undefined || merged.adbDirectUrls === null) merged.adbDirectUrls = "";
        if (merged.screenshotAfterScraping === undefined) merged.screenshotAfterScraping = false;
        if (merged.screenshotTargetTaskId === undefined) merged.screenshotTargetTaskId = "";
        if (merged.spiderSourceTaskId === undefined) merged.spiderSourceTaskId = "";
        if (merged.dnsFiles === undefined) merged.dnsFiles = [];
        if (merged.dnsConditions === undefined || merged.dnsConditions === null) merged.dnsConditions = "";
        return merged;
      }
    } catch (e) {
      console.warn("Failed to parse sessionStorage");
    }
    return getDefaultState();
  };

  const [state, setState] = useState(getInitialState);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const fileInputRef = useRef(null);

  const scrapingPeriods =
    state.advancedSettings && state.advancedSettings.scrapingPeriods
      ? state.advancedSettings.scrapingPeriods
      : ["any"];
  const siteFilter = PLATFORM_SITES[state.selectedPlatform] ? PLATFORM_SITES[state.selectedPlatform] : "";

  const isAdbMode = state.selectedPlatform === "instagram_adb";
  const isScreenshotMode = state.selectedPlatform === "facebook_screenshot";
  const isSpiderMode = state.selectedPlatform === "instagram_adb_spider";
  const isDnsMode = state.selectedPlatform === "dns_filtering";
  const isSimpleProcessingMode = isAdbMode || isScreenshotMode || isSpiderMode || isDnsMode;

  const adbDirectUrls = state.adbDirectUrls || "";
  const keywords = state.keywords || "";
  const dnsConditions = typeof state.dnsConditions === 'string' ? state.dnsConditions : "";

  const uniqueAdbUrls = getUniqueUrls(adbDirectUrls);
  const totalAdbUrls = adbDirectUrls.split("\n").filter(u => u.trim()).length;
  const uniqueAdbUrlsCount = uniqueAdbUrls.length;
  const duplicateAdbUrlsCount = totalAdbUrls - uniqueAdbUrlsCount;

  const dnsConditionsList = typeof dnsConditions === 'string' 
  ? dnsConditions.split("\n").filter(c => c.trim()).map(parseDnsCondition).filter(Boolean)
  : [];

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (mode === "combined" && onStateChange) onStateChange(state);
  }, [state, mode, onStateChange]);

  const selectedSocial = SOCIALS.find((soc) => soc.id === state.selectedSocial);
  const selectedSubtype = selectedSocial
    ? selectedSocial.subtypes.find((sub) => sub.id === state.selectedPlatform)
    : null;
  const showParseAuthorOption = selectedSubtype ? selectedSubtype.hasAuthorOption : false;

  // Загрузка задач для ADB
  useEffect(() => {
    if (!IS_ADMIN || !isAdbMode || state.adbSourceType !== "task") return;
    const token = getCookie("authToken");
    if (!token) return;

    updateState("adbTasksLoading", true);
    fetch(`${API_BASE}/tasks/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load tasks");
        return res.json();
      })
      .then((data) => {
        const adbTasks = (Array.isArray(data) ? data : []).filter((task) => {
          const subtype =
            task.scraping_subtype?.value ||
            task.filtering_config_name?.subtype ||
            task.subtype;
          
          return subtype === "instagram_pages" || subtype === "instagram_adb_spider";
        });
        updateState("availableAdbTasks", adbTasks);
        updateState("adbTasksLoading", false);
      })
      .catch((err) => {
        console.error("Failed to load ADB tasks:", err);
        updateState("adbTasksLoading", false);
      });
  }, [isAdbMode, IS_ADMIN, state.adbSourceType]);

  // Загрузка задач для Facebook Screenshot
  useEffect(() => {
    if (!IS_ADMIN || !isScreenshotMode) return;
    const token = getCookie("authToken");
    if (!token) return;

    updateState("screenshotTasksLoading", true);
    fetch(`${API_BASE}/tasks/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load tasks");
        return res.json();
      })
      .then((data) => {
        const screenshotTasks = (Array.isArray(data) ? data : []).filter((task) => {
          const subtype =
            task.scraping_subtype?.value ||
            task.filtering_config_name?.subtype ||
            task.subtype;
          return subtype === "facebook_pages" || subtype === "facebook_posts";
        });
        updateState("availableScreenshotTasks", screenshotTasks);
        updateState("screenshotTasksLoading", false);
      })
      .catch((err) => {
        console.error("Failed to load screenshot tasks:", err);
        updateState("screenshotTasksLoading", false);
      });
  }, [isScreenshotMode, IS_ADMIN]);

  // Загрузка задач для Spider
  useEffect(() => {
    if (!IS_ADMIN || !isSpiderMode) return;
    const token = getCookie("authToken");
    if (!token) return;

    updateState("spiderTasksLoading", true);
    fetch(`${API_BASE}/tasks/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load tasks");
        return res.json();
      })
      .then((data) => {
        const spiderTasks = (Array.isArray(data) ? data : []).filter((task) => {
          const subtype =
            task.scraping_subtype?.value ||
            task.filtering_config_name?.subtype ||
            task.subtype;
          return subtype === "instagram_pages" || subtype === "instagram_adb";
        });
        updateState("spiderSourceTasks", spiderTasks);
        updateState("spiderTasksLoading", false);
      })
      .catch((err) => {
        console.error("Failed to load spider source tasks:", err);
        updateState("spiderTasksLoading", false);
      });
  }, [isSpiderMode, IS_ADMIN]);

  useEffect(() => {
    if (!state.promptTemplate && !state.aiPrompt) {
      const template = getDefaultTemplate(state.selectedPlatform);
      const target = state.whoToFind.trim() ? state.whoToFind.trim() : "{target}";
      setState((prev) => ({
        ...prev,
        promptTemplate: template,
        aiPrompt: buildPrompt(template, target),
        prevTarget: target,
      }));
    }
  }, []);

  useEffect(() => {
    if (state.isManuallyEdited) return;
    const template = state._prefillPrompt
      || getDefaultTemplate(state.selectedPlatform);
    const target = state.whoToFind.trim() ? state.whoToFind.trim() : "{target}";
    setState((prev) => ({
      ...prev,
      promptTemplate: template,
      aiPrompt: buildPrompt(template, target),
      prevTarget: target,
    }));
  }, [state.selectedPlatform, state.whoToFind]);

  useEffect(() => {
    const handleClear = () => setState(getDefaultState());
    window.addEventListener("clearAllTasks", handleClear);
    return () => window.removeEventListener("clearAllTasks", handleClear);
  }, []);

  // ==================== HANDLERS ====================

  const updateState = (key, value) => setState((prev) => ({ ...prev, [key]: value }));

  const handleWhoToFindChange = (e) => {
    const newTarget = e.target.value;
    setState((prev) => {
      if (prev.isManuallyEdited) {
        return { ...prev, whoToFind: newTarget };
      }
      const replaceWith = newTarget.trim() ? newTarget.trim() : "{target}";
      const newPrompt = buildPrompt(prev.promptTemplate, replaceWith);
      return {
        ...prev,
        whoToFind: newTarget,
        aiPrompt: newPrompt,
        prevTarget: replaceWith,
      };
    });
  };

  const handleAiPromptChange = (e) => {
    const newPrompt = e.target.value;
    setState((prev) => ({
      ...prev,
      aiPrompt: newPrompt,
      promptTemplate: newPrompt,
      isManuallyEdited: true,
    }));
  };

  const handleResetPrompt = () => {
    setState((prev) => {
      const template = getDefaultTemplate(prev.selectedPlatform);
      const target = prev.whoToFind.trim() ? prev.whoToFind.trim() : "{target}";
      return {
        ...prev,
        promptTemplate: template,
        aiPrompt: buildPrompt(template, target),
        prevTarget: target,
        isManuallyEdited: false,
      };
    });
  };

  const updateAdvancedSetting = (key, value) => {
    setState((prev) => ({
      ...prev,
      advancedSettings: {
        ...prev.advancedSettings,
        [key]: value,
        scrapingPeriods:
          prev.advancedSettings && prev.advancedSettings.scrapingPeriods
            ? prev.advancedSettings.scrapingPeriods
            : ["any"],
      },
    }));
  };

  const toggleScrapingPeriod = (period) => {
    setState((prev) => {
      const periods = [
        ...(prev.advancedSettings && prev.advancedSettings.scrapingPeriods
          ? prev.advancedSettings.scrapingPeriods
          : ["any"]),
      ];
      if (period === "any") {
        return {
          ...prev,
          advancedSettings: { ...prev.advancedSettings, scrapingPeriods: ["any"] },
        };
      }
      let newPeriods = periods.filter((p) => p !== "any");
      if (newPeriods.includes(period)) {
        newPeriods = newPeriods.filter((p) => p !== period);
      } else {
        newPeriods = [...newPeriods, period];
      }
      if (newPeriods.length === 0) newPeriods = ["any"];
      return {
        ...prev,
        advancedSettings: { ...prev.advancedSettings, scrapingPeriods: newPeriods },
      };
    });
  };

  // ==================== DNS HANDLERS ====================
  const handleDnsFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
    }));
    
    setState(prev => ({
      ...prev,
      dnsFiles: [...prev.dnsFiles, ...newFiles],
    }));
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDnsFileRemove = (fileId) => {
    setState(prev => ({
      ...prev,
      dnsFiles: prev.dnsFiles.filter(f => f.id !== fileId),
    }));
  };

  const calculateCredits = () => {
    if (isAdbMode || isScreenshotMode || isSpiderMode || isDnsMode) return { tokens: 0, googleJobs: 0, totalLinks: 0, parseJobs: 0, queryCount: 0, pages: 0, periodsMultiplier: 0 };
    const keywordLines =
      keywords.split("\n").filter((k) => k.trim()).length || 0;
    const pages = parseInt(state.advancedSettings && state.advancedSettings.maxPages) || 1;
    const periods =
      state.advancedSettings && state.advancedSettings.scrapingPeriods
        ? state.advancedSettings.scrapingPeriods
        : ["any"];
    const periodsMultiplier =
      periods.includes("any") || periods.length === 0 ? 1 : periods.length;

    const googleJobs = keywordLines * pages * periodsMultiplier;
    const totalLinks = googleJobs * 10;
    const parseJobs = totalLinks;

    const googlePrice = parseFloat(
      userInfo && userInfo.price_per_google_page ? userInfo.price_per_google_page : 0.00625
    );
    let parsePrice = 0.0007;
    if (userInfo) {
      switch (state.selectedPlatform) {
        case "facebook_posts":
          parsePrice = parseFloat(userInfo.price_per_facebook_post) || 0.0007;
          break;
        case "facebook_pages":
          parsePrice = parseFloat(userInfo.price_per_facebook_page) || 0.0007;
          break;
        case "linkedin_posts":
          parsePrice = parseFloat(userInfo.price_per_linkedin_post) || 0.0007;
          break;
        case "instagram_posts":
          parsePrice = parseFloat(userInfo.price_per_instagram_post) || 0.0007;
          break;
        case "instagram_pages":
          parsePrice = parseFloat(userInfo.price_per_instagram_page) || 0.0007;
          break;
        case "instagram_reels":
          parsePrice =
            parseFloat(userInfo.price_per_instagram_reel) ||
            parseFloat(userInfo.price_per_instagram_post) ||
            0.0007;
          break;
        default:
          parsePrice = 0.0007;
          break;
      }
    }

    const googleCost = googleJobs * googlePrice;
    let parseCost = parseJobs * parsePrice;
    if (showParseAuthorOption && state.parseAuthorPages) parseCost *= 2;

    const totalCost = googleCost + parseCost;
    const tokens = Math.round(totalCost * 100);

    return {
      tokens,
      googleJobs,
      totalLinks,
      parseJobs,
      queryCount: keywordLines,
      pages,
      periodsMultiplier,
    };
  };

  const generateAIQueries = async () => {
    if (!state.aiPrompt.trim()) return;
    setAiLoading(true);
    let streamBuffer = "";

    const finalPrompt = state.aiPrompt
      .split("{target}")
      .join(state.whoToFind.trim() ? state.whoToFind.trim() : "{target}");

    try {
      const token = getCookie("authToken");
      const res = await fetch("https://flautreach.com/flautreach/api/ai/generate-queries/", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: finalPrompt,
          platform: state.selectedPlatform ? state.selectedPlatform : "general",
          target: state.whoToFind.trim(),
          language: lang,
        }),
      });

      if (!res.ok) throw new Error("AI generation failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        while (true) {
          const dataIdx = buffer.indexOf("data: ");
          if (dataIdx === -1) break;
          const jsonStart = dataIdx + 6;
          let jsonEnd = buffer.indexOf("\n", jsonStart);
          if (jsonEnd === -1) break;
          const jsonStr = buffer.slice(jsonStart, jsonEnd).trim();
          buffer = buffer.slice(jsonEnd + 1);
          if (!jsonStr) continue;

          try {
            const json = JSON.parse(jsonStr);
            if (json.chunk) {
              streamBuffer += json.chunk;
              const lines = streamBuffer.split("\n");
              const completeLines = lines.slice(0, -1);
              const pendingLine = lines[lines.length - 1];
              setState((prev) => ({
                ...prev,
                keywords:
                  completeLines.join("\n") +
                  (completeLines.length > 0 ? "\n" : "") +
                  pendingLine,
              }));
            }
            if (json.done) {
              const result = json.result ? json.result : streamBuffer;
              const resultLines = result.split("\n").filter((l) => l.trim());
              if (resultLines.length > 0) {
                setState((prev) => ({
                  ...prev,
                  keywords: resultLines.join("\n"),
                }));
              }
            }
          } catch (e) {
            /* skip broken chunks */
          }
        }
      }
    } catch (err) {
      console.error("AI error:", err);
    } finally {
      setAiLoading(false);
    }
  };

  const checkInGoogle = (query) => {
    if (!query.trim()) return;
    window.open(
      `https://www.google.com/search?q=${encodeURIComponent(query.trim())}`,
      "_blank"
    );
  };

  const handleSave = async () => {
    // === DNS Mode ===
if (isDnsMode) {
  if (state.dnsFiles.length === 0) {
    setApiError("Please upload at least one HTML file");
    return;
  }
  
  if (dnsConditionsList.length === 0) {
    setApiError("Please add at least one valid filtering condition");
    return;
  }
  
  setSaving(true);
  setApiError(null);
  
  try {
    const token = getCookie("authToken");
    
    // Создаем FormData для загрузки
    const formData = new FormData();
    formData.append("name", taskName?.trim() || `DNS Resolving #${Date.now()}`);
    formData.append("conditions", dnsConditions); // ← строка с условиями
    
    // Отправляем только первый файл (или все файлы?)
    // Если бэкенд ожидает один файл:
    if (state.dnsFiles.length > 0) {
      formData.append("file", state.dnsFiles[0].file);
    }
    
    // Если нужно несколько файлов, можно отправить каждый отдельно
    // или изменить бэкенд для приема массива файлов
    
    const res = await fetch(`${API_BASE}/dns-resolving/upload/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // НЕ указываем Content-Type, браузер сам установит с boundary
      },
      body: formData,
    });
    
    if (!res.ok) {
      const errData = await res.json().catch(() => ({ detail: "Unknown error" }));
      throw new Error(JSON.stringify(errData, null, 2));
    }
    
    const data = await res.json();
    sessionStorage.removeItem(STORAGE_KEY);
    onSave(data);
  } catch (err) {
    console.error("DNS task creation error:", err);
    setApiError(err.message);
  } finally {
    setSaving(false);
  }
  return;
}

    // === ADB Mode ===
    if (isAdbMode) {
      if (state.adbSourceType === "task" && !state.adbTargetTaskId) {
        setApiError(s.selectAdbTaskError || "Please select a task");
        return;
      }
      
      if (state.adbSourceType === "urls") {
        const uniqueUrls = getUniqueUrls(adbDirectUrls);
        if (uniqueUrls.length === 0) {
          setApiError("Please enter at least one Instagram URL");
          return;
        }
      }
      
      setSaving(true);
      setApiError(null);
      
      try {
        const token = getCookie("authToken");
        const payload = {
          name: taskName?.trim() || `ADB Processing #${Date.now()}`,
          task_type: "scraping",
          scraping: {
            subtype: "instagram_adb",
            search_language: "en",
            search_periods: [],
            max_pages_per_query: 1,
          },
        };
        
        if (state.adbSourceType === "task") {
          payload.target_task_id = parseInt(state.adbTargetTaskId, 10);
        } else {
          const uniqueUrls = getUniqueUrls(adbDirectUrls);
          payload.direct_urls = uniqueUrls;
        }
        
        const res = await fetch(`${API_BASE}/tasks/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        
        if (!res.ok) {
          const errData = await res.json().catch(() => ({ detail: "Unknown error" }));
          throw new Error(JSON.stringify(errData, null, 2));
        }
        
        const data = await res.json();
        sessionStorage.removeItem(STORAGE_KEY);
        onSave(data);
      } catch (err) {
        console.error("ADB task creation error:", err);
        setApiError(err.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    // === Facebook Screenshot Mode ===
    if (isScreenshotMode) {
      if (!state.screenshotTargetTaskId) {
        setApiError(s.selectScreenshotTaskError || "Please select a task");
        return;
      }
      setSaving(true);
      setApiError(null);
      try {
        const token = getCookie("authToken");
        const res = await fetch(`${API_BASE}/tasks/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: taskName?.trim() || `Facebook Screenshot Processing #${state.screenshotTargetTaskId}`,
            task_type: "scraping",
            scraping: {
              subtype: "facebook_screenshot",
              search_language: "en",
              search_periods: [],
              max_pages_per_query: 1,
            },
            target_task_id: parseInt(state.screenshotTargetTaskId, 10),
          }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({ detail: "Unknown error" }));
          throw new Error(JSON.stringify(errData, null, 2));
        }
        const data = await res.json();
        sessionStorage.removeItem(STORAGE_KEY);
        onSave(data);
      } catch (err) {
        console.error("Screenshot task creation error:", err);
        setApiError(err.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    // === ADB Spider Mode ===
    if (isSpiderMode) {
      if (!state.spiderSourceTaskId) {
        setApiError(s.selectSpiderSourceTaskError || "Please select a source task");
        return;
      }
      const keywordList = keywords.split("\n").filter((k) => k.trim());
      if (keywordList.length === 0) {
        setApiError(s.spiderKeywordsRequired || "Please enter at least one keyword");
        return;
      }
      setSaving(true);
      setApiError(null);
      try {
        const token = getCookie("authToken");
        const res = await fetch(`${API_BASE}/tasks/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: taskName?.trim() || `ADB Spider #${state.spiderSourceTaskId}`,
            task_type: "scraping",
            scraping: {
              subtype: "instagram_adb_spider",
              search_language: state.advancedSettings?.searchLanguage || "en",
              search_periods: [],
              max_pages_per_query: 1,
            },
            target_task_id: parseInt(state.spiderSourceTaskId, 10),
            search_queries: keywordList,
          }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({ detail: "Unknown error" }));
          throw new Error(JSON.stringify(errData, null, 2));
        }
        const data = await res.json();
        sessionStorage.removeItem(STORAGE_KEY);
        onSave(data);
      } catch (err) {
        console.error("Spider task creation error:", err);
        setApiError(err.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    // === Обычное создание скрапинга ===
    if (!keywords.trim() || !state.selectedPlatform) return;
    setSaving(true);
    setApiError(null);
    const keywordList = keywords.split("\n").filter((k) => k.trim());

    const VALID_PERIODS = ["h", "d", "w", "m", "y"];
    const rawPeriods =
      state.advancedSettings && state.advancedSettings.scrapingPeriods
        ? state.advancedSettings.scrapingPeriods
        : ["any"];
    let cleanPeriods;
    if (rawPeriods.includes("any")) {
      cleanPeriods = ["any"];
    } else {
      cleanPeriods = rawPeriods.filter((p) => VALID_PERIODS.includes(p));
      if (cleanPeriods.length === 0) cleanPeriods = ["any"];
    }
    const PERIOD_FIX_MAP = {
      all: "any",
      year: "y",
      month: "m",
      week: "w",
      day: "d",
      hour: "h",
    };
    cleanPeriods = cleanPeriods.map((p) => (PERIOD_FIX_MAP[p] ? PERIOD_FIX_MAP[p] : p));
    cleanPeriods = [...new Set(cleanPeriods)];
    const periodsForApi = cleanPeriods.filter((p) => p !== "any");

    const payload = {
      name: taskName,
      task_type: includeFiltering ? "scraping_and_filtering" : "scraping",
      scraping: {
        subtype: state.selectedPlatform,
        search_language:
          state.advancedSettings && state.advancedSettings.searchLanguage
            ? state.advancedSettings.searchLanguage
            : "en",
        search_periods: periodsForApi,
        max_pages_per_query: parseInt(
          state.advancedSettings && state.advancedSettings.maxPages
            ? state.advancedSettings.maxPages
            : 1
        ),
      },
      search_queries: keywordList,
    };

    if (state.selectedPlatform === "instagram_pages") {
      payload.additional_info = {
        instagram_posts_included: state.parseAuthorPages ? "True" : "False",
      };
    }

    if (IS_ADMIN && state.selectedPlatform === "instagram_pages" && state.adbAfterScraping) {
      payload.adb_after_scraping = true;
    }

    if (IS_ADMIN && (state.selectedPlatform === "facebook_posts" || state.selectedPlatform === "facebook_pages") && state.screenshotAfterScraping) {
      payload.facebook_screenshot_after_scraping = true;
    }

    if (includeFiltering && window.filteringSectionData) {
      payload.filtering = window.filteringSectionData;
    }

    if (state.scheduleType === "repeat") {
      payload.schedule = {
        interval: state.repeatInterval,
        count: parseInt(state.repeatCount) || 1,
      };
    }

    try {
      const token = getCookie("authToken");
      const res = await fetch("https://flautreach.com/flautreach/api/tasks/", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ detail: "Unknown error" }));
        throw new Error(JSON.stringify(errData, null, 2));
      }
      const data = await res.json();
      sessionStorage.removeItem(STORAGE_KEY);
      await onSave(data);
    } catch (err) {
      console.error("Create task error:", err);
      setApiError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const queryCount = keywords.split("\n").filter((k) => k.trim()).length;
  const credits = calculateCredits();

  const allLanguages = ISO6391.getAllCodes().map((code) => ({
    code,
    name: ISO6391.getName(code),
    nativeName: ISO6391.getNativeName(code),
  }));
  const popularLanguages = allLanguages.filter((l) => POPULAR_LANGUAGES.includes(l.code));
  const otherLanguages = allLanguages.filter((l) => !POPULAR_LANGUAGES.includes(l.code));

  const showOwnActions = mode !== "combined" || isSimpleProcessingMode;
  
  // ==================== RENDER ====================
  return (
    <>
      {apiError && <div className="api-error-box">⚠️ {apiError}</div>}

      {/* Platform Block */}
      <div className="section-card">
        <h3>
          {s.platform}
          <span className="required-badge" title={s.required}>*</span>
        </h3>

        <p className="platform-step-label">{s.step1ChooseNetwork}</p>
        <div className="social-grid">
          {SOCIALS.map((social) => {
            const isSelected = state.selectedSocial === social.id;
            return (
              <button
                key={social.id}
                className={`social-btn${isSelected ? " selected" : ""}`}
                data-social={social.id}
                onClick={() => {
                  if (isSelected) {
                    updateState("selectedSocial", "");
                    updateState("selectedPlatform", "");
                  } else {
                    updateState("selectedSocial", social.id);
                    updateState("selectedPlatform", "");
                  }
                }}
              >
                <div className="social-btn-icon">{social.icon}</div>
                <span className="social-btn-name">{social.name}</span>
              </button>
            );
          })}
        </div>

        {selectedSocial && (
          <>
            <p className="platform-step-label">
              {s.step2WhatToFind.replace("{platform}", selectedSocial.name)}
            </p>
            <div className="platform-subtype-grid">
              {selectedSocial.subtypes.map((subtype) => {
                const isSelected = state.selectedPlatform === subtype.id;
                return (
                  <button
                    key={subtype.id}
                    className={`platform-subtype-btn${isSelected ? " selected" : ""}`}
                    onClick={() =>
                      updateState("selectedPlatform", isSelected ? "" : subtype.id)
                    }
                  >
                    <div className="platform-subtype-indicator" />
                    <div className="platform-subtype-info">
                      <div className="platform-subtype-name">{subtype.name}</div>
                      <div className="platform-subtype-desc">
                        {subtype.description}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="platform-subtype-check">✓</div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Scrape Author Pages */}
            {showParseAuthorOption && (
              <div className={`parse-author-toggle${state.parseAuthorPages ? " enabled" : ""}`}>
                <label className="parse-author-label">
                  <div
                    className={`parse-author-switch${state.parseAuthorPages ? " enabled" : ""}`}
                    onClick={() => updateState("parseAuthorPages", !state.parseAuthorPages)}
                  >
                    <div className="parse-author-switch-knob" />
                  </div>
                  <div className="parse-author-text">
                    <div className="parse-author-title-row">
                      <span className="parse-author-title">
                        {s.scrapeAuthorPages}
                      </span>
                      <span className={`parse-author-badge${state.parseAuthorPages ? " enabled" : ""}`}>
                        {state.parseAuthorPages ? s.on : s.off}
                      </span>
                    </div>
                    <p className="parse-author-desc">
                      {s.scrapeAuthorPagesDesc}
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Process via ADB toggle */}
            {IS_ADMIN && state.selectedPlatform === "instagram_pages" && (
              <div className={`parse-author-toggle${state.adbAfterScraping ? " enabled" : ""}`}>
                <label className="parse-author-label">
                  <div
                    className={`parse-author-switch${state.adbAfterScraping ? " enabled" : ""}`}
                    onClick={() => updateState("adbAfterScraping", !state.adbAfterScraping)}
                  >
                    <div className="parse-author-switch-knob" />
                  </div>
                  <div className="parse-author-text">
                    <div className="parse-author-title-row">
                      <span className="parse-author-title">
                        {s.adbProcessLabel || "Process via ADB"}
                      </span>
                      <span className={`parse-author-badge${state.adbAfterScraping ? " enabled" : ""}`}>
                        {state.adbAfterScraping ? s.on : s.off}
                      </span>
                    </div>
                    <p className="parse-author-desc">
                      {s.adbProcessDesc || "After scraping completes, automatically start ADB processing"}
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Facebook Page Screenshot toggle */}
            {IS_ADMIN && (state.selectedPlatform === "facebook_posts" || state.selectedPlatform === "facebook_pages") && (
              <div className={`parse-author-toggle${state.screenshotAfterScraping ? " enabled" : ""}`}>
                <label className="parse-author-label">
                  <div
                    className={`parse-author-switch${state.screenshotAfterScraping ? " enabled" : ""}`}
                    onClick={() => updateState("screenshotAfterScraping", !state.screenshotAfterScraping)}
                  >
                    <div className="parse-author-switch-knob" />
                  </div>
                  <div className="parse-author-text">
                    <div className="parse-author-title-row">
                      <span className="parse-author-title">
                        {s.facebookScreenshotToggleLabel || "Facebook Page Screenshot"}
                      </span>
                      <span className={`parse-author-badge${state.screenshotAfterScraping ? " enabled" : ""}`}>
                        {state.screenshotAfterScraping ? s.on : s.off}
                      </span>
                    </div>
                    <p className="parse-author-desc">
                      {s.facebookScreenshotToggleDesc || "After scraping, take screenshots of Facebook pages"}
                    </p>
                  </div>
                </label>
              </div>
            )}
          </>
        )}
      </div>

      {/* DNS Mode */}
      {isDnsMode && (
        <>
          <div className="section-card">
            <h3>
              📁 Upload TXT Files
              <span className="required-badge">*</span>
            </h3>
            <p style={{ fontSize: 14, color: "var(--muted)", marginBottom: 12 }}>
              Upload TXT files to be filtered based on your conditions
            </p>
            
            <div 
              className="dns-upload-area"
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: "2px dashed #6C5CE7",
                borderRadius: 12,
                padding: 30,
                textAlign: "center",
                cursor: "pointer",
                background: "var(--bg-secondary)",
                transition: "all 0.3s ease",
              }}
            >
              <HiUpload size={40} color="#6C5CE7" style={{ marginBottom: 12 }} />
              <p style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>
                Click to upload or drag and drop
              </p>
              <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                TXT files only
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt"
                multiple
                onChange={handleDnsFileUpload}
                style={{ display: "none" }}
              />
            </div>
            
            {state.dnsFiles.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <p style={{ fontWeight: 600, marginBottom: 8 }}>
                  Uploaded Files ({state.dnsFiles.length}):
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {state.dnsFiles.map(fileObj => (
                    <div 
                      key={fileObj.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        background: "var(--bg-secondary)",
                        borderRadius: 8,
                        border: "1px solid var(--border)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18 }}>📄</span>
                        <span style={{ fontSize: 14, color: "var(--text-primary)" }}>
                          {fileObj.name}
                        </span>
                        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                          ({(fileObj.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        onClick={() => handleDnsFileRemove(fileObj.id)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#FF5252",
                          padding: "4px 8px",
                          borderRadius: 4,
                        }}
                      >
                        <HiTrash size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="section-card">
            <h3>
              🔍 Filtering Conditions
              <span className="required-badge">*</span>
            </h3>
            <p style={{ fontSize: 14, color: "var(--muted)", marginBottom: 12 }}>
              Enter one condition per line. Each condition can contain one or multiple keywords.
            </p>
            
            <div style={{ 
              background: "#F0F0F5", 
              padding: 12, 
              borderRadius: 8, 
              marginBottom: 16,
              fontSize: 13,
              color: "var(--text-secondary)",
            }}>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>📋 Syntax Guide:</p>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li><code>keyword</code> - match if keyword found (case insensitive)</li>
                <li><code>cs:Keyword</code> - case sensitive match</li>
                <li><code>!keyword</code> - minus word: if found → reject</li>
                <li><code>keyword1, keyword2</code> - match if ALL keywords found (AND)</li>
                <li><code>keyword1 & keyword2</code> - same as above (AND)</li>
              </ul>
            </div>
            
            <textarea
              className="intent-textarea"
              placeholder={"keyword1\ncs:Keyword2\n!keyword3\nkeyword4, keyword5"}
              value={dnsConditions}
              onChange={(e) => updateState("dnsConditions", e.target.value)}
              style={{ minHeight: 200, fontFamily: "'Ubuntu', 'Montserrat', monospace", fontSize: 14 }}
            />
            
            {dnsConditionsList.length > 0 && (
              <div style={{ marginTop: 12, padding: 8, background: "var(--bg-secondary)", borderRadius: 6 }}>
                <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: 0 }}>
                  ✅ {dnsConditionsList.length} valid condition(s) parsed
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* ADB Mode – выбор источника */}
      {isAdbMode && (
        <div className="section-card">
          <h3>
            ADB Processing Source
            <span className="required-badge">*</span>
          </h3>
          
          <div className="schedule-grid" style={{ marginBottom: 16 }}>
            <button
              className={`schedule-btn${state.adbSourceType === "task" ? " selected" : ""}`}
              onClick={() => updateState("adbSourceType", "task")}
            >
              <span className="schedule-btn-icon">📋</span>
              <span>From Task</span>
              <span className="schedule-btn-desc">Use existing task results</span>
            </button>
            <button
              className={`schedule-btn${state.adbSourceType === "urls" ? " selected" : ""}`}
              onClick={() => updateState("adbSourceType", "urls")}
            >
              <span className="schedule-btn-icon">🔗</span>
              <span>Direct URLs</span>
              <span className="schedule-btn-desc">Enter Instagram URLs manually</span>
            </button>
          </div>
          
          {state.adbSourceType === "task" && (
            <>
              {state.adbTasksLoading ? (
                <p>{s.loading || "Loading tasks..."}</p>
              ) : (
                <select
                  className="form-input"
                  value={state.adbTargetTaskId}
                  onChange={(e) => updateState("adbTargetTaskId", e.target.value)}
                >
                  <option value="">{s.selectTask || "Choose a task"}</option>
                  {state.availableAdbTasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.name || `${s.task || "Task"} #${task.id}`}
                      {task.jobs_summary?.completed != null
                        ? ` (${task.jobs_summary.completed} profiles)`
                        : ""}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
          
          {state.adbSourceType === "urls" && (
            <>
              <p style={{ fontSize: 14, color: "var(--muted)", marginBottom: 8 }}>
                Enter Instagram profile URLs, one per line. 
                <br />Examples:
                <br />• https://www.instagram.com/username
                <br />• https://www.instagram.com/username/reel/abc123/
                <br />• @username
                <br />• username
              </p>
              <textarea
                className="intent-textarea"
                placeholder={"https://www.instagram.com/username1\nhttps://www.instagram.com/username2\n@username3\nusername4"}
                value={adbDirectUrls}
                onChange={(e) => updateState("adbDirectUrls", e.target.value)}
                style={{ minHeight: 200 }}
              />
              
              {totalAdbUrls > 0 && (
                <div style={{ 
                  marginTop: 12, 
                  padding: 12, 
                  background: "var(--bg-secondary)", 
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                      URL Summary
                    </span>
                  </div>
                  
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ 
                      flex: 1, 
                      minWidth: 120, 
                      padding: 8, 
                      background: "var(--bg-primary)", 
                      borderRadius: 6,
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "var(--text-primary)" }}>
                        {totalAdbUrls}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                        Total URLs
                      </div>
                    </div>
                    
                    <div style={{ 
                      flex: 1, 
                      minWidth: 120, 
                      padding: 8, 
                      background: "var(--bg-primary)", 
                      borderRadius: 6,
                      textAlign: "center",
                      border: "2px solid #4CAF50"
                    }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#4CAF50" }}>
                        {uniqueAdbUrlsCount}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                        Unique URLs
                      </div>
                    </div>
                    
                    {duplicateAdbUrlsCount > 0 && (
                      <div style={{ 
                        flex: 1, 
                        minWidth: 120, 
                        padding: 8, 
                        background: "var(--bg-primary)", 
                        borderRadius: 6,
                        textAlign: "center",
                        border: "2px solid #FF9800"
                      }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: "#FF9800" }}>
                          {duplicateAdbUrlsCount}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                          Duplicates
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {duplicateAdbUrlsCount > 0 && (
                    <div style={{ 
                      marginTop: 8, 
                      padding: 8, 
                      background: "#FFF3CD", 
                      borderRadius: 6,
                      fontSize: 12,
                      color: "#856404",
                      border: "1px solid #FFEEBA"
                    }}>
                      ⚠️ {duplicateAdbUrlsCount} duplicate URL{duplicateAdbUrlsCount > 1 ? 's' : ''} will be automatically removed
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Facebook Screenshot Mode */}
      {isScreenshotMode && (
        <div className="section-card">
          <h3>
            {s.selectScreenshotTask || "Select existing Facebook task"}
            <span className="required-badge">*</span>
          </h3>
          {state.screenshotTasksLoading ? (
            <p>{s.loading || "Loading tasks..."}</p>
          ) : (
            <select
              className="form-input"
              value={state.screenshotTargetTaskId}
              onChange={(e) => updateState("screenshotTargetTaskId", e.target.value)}
            >
              <option value="">{s.selectTask || "Choose a task"}</option>
              {state.availableScreenshotTasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.name || `${s.task || "Task"} #${task.id}`}
                  {task.jobs_summary?.completed != null
                    ? ` (${task.jobs_summary.completed} items)`
                    : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* ADB Spider Mode */}
      {isSpiderMode && (
        <>
          <div className="section-card">
            <h3>
              {s.selectSpiderSourceTask || "Select source task"}
              <span className="required-badge">*</span>
            </h3>
            <p style={{ fontSize: 14, color: "var(--muted)", marginBottom: 8 }}>
              {s.spiderSourceTaskDesc || "Choose an Instagram Profiles or ADB task as source"}
            </p>
            {state.spiderTasksLoading ? (
              <p>{s.loading || "Loading tasks..."}</p>
            ) : (
              <select
                className="form-input"
                value={state.spiderSourceTaskId}
                onChange={(e) => updateState("spiderSourceTaskId", e.target.value)}
              >
                <option value="">{s.selectTask || "Choose a task"}</option>
                {state.spiderSourceTasks.map((task) => (
                  <option key={task.id} value={task.id}>
                    {task.name || `${s.task || "Task"} #${task.id}`}
                    {task.jobs_summary?.completed != null
                      ? ` (${task.jobs_summary.completed} items)`
                      : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="section-card">
            <h3>
              {s.spiderKeywordsLabel || "Keywords"}
              <span className="required-badge" title={s.required}>*</span>
            </h3>
            <p style={{ fontSize: 14, color: "var(--muted)", marginBottom: 8 }}>
              {s.spiderKeywordsDesc || "Enter keywords, one per line"}
            </p>
            <textarea
              className="intent-textarea"
              placeholder={s.spiderKeywordsPlaceholder || "Enter keywords, one per line"}
              value={keywords}
              onChange={(e) => updateState("keywords", e.target.value)}
              style={{ minHeight: 200 }}
            />
            <div className="query-count">
              {keywords.split("\n").filter((k) => k.trim()).length}{" "}
              {s.activeQueries || "keywords"}
            </div>
          </div>
        </>
      )}

      {/* Остальные поля скрапинга */}
      {!isAdbMode && !isScreenshotMode && !isSpiderMode && !isDnsMode && (
        <>
          {/* Who to find */}
          <div className="section-card">
            <h3>
              {s.whoToFind}
              <span className="optional-badge">{s.optional}</span>
            </h3>
            <textarea
              className="intent-textarea"
              placeholder={s.whoToFindPlaceholder}
              value={state.whoToFind}
              onChange={handleWhoToFindChange}
              style={{ minHeight: 80, marginBottom: 8 }}
            />
          </div>

          {/* AI Prompt */}
          <div className="section-card">
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <h3 style={{ margin: 0 }}>
                  {s.aiPromptLabel}
                  <span className="optional-badge">{s.optional}</span>
                </h3>
                {!state.isManuallyEdited && (
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#D4EDDA", color: "#155724", border: "1px solid #C3E6CB", display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
                    🔗 {s.autoSync}
                  </span>
                )}
                {state.isManuallyEdited && (
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#FFF3CD", color: "#856404", border: "1px solid #FFEEBA", display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
                    🔓 {s.manualMode}
                  </span>
                )}
              </div>
              {state.isManuallyEdited && (
                <button className="btn-reset-prompt" onClick={handleResetPrompt} title={s.resetPrompt} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 12px", fontSize: 13, background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", color: "var(--text-secondary)", width: "100%" }}>
                  <HiRefresh size={14} /> {s.resetPrompt}
                </button>
              )}
            </div>
            <div style={{ height: 2, marginBottom: 8, borderRadius: 1, background: state.isManuallyEdited ? "linear-gradient(90deg, #FFC107, #FF9800)" : "linear-gradient(90deg, #4CAF50, #2196F3)", opacity: 0.5, transition: "background 0.3s ease" }} />
            <textarea className="intent-textarea" value={state.aiPrompt} onChange={handleAiPromptChange} style={{ minHeight: 200, fontSize: 14, fontFamily: "'Ubuntu', 'Montserrat', monospace", lineHeight: 1.6, borderColor: state.isManuallyEdited ? "var(--warning)" : undefined }} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="ai-generate-btn" onClick={generateAIQueries} disabled={!state.aiPrompt.trim() || aiLoading}>
                {aiLoading ? `⏳ ${s.generating}` : `🤖 ${s.generateWithAI}`}
              </button>
            </div>
          </div>

          {/* Keywords */}
          <div className="section-card">
            <h3>
              {s.searchQueries}
              <span className="required-badge" title={s.required}>*</span>
            </h3>
            <p style={{ fontSize: 14, color: "var(--muted)", marginBottom: 8 }}>
              {s.oneQueryPerLine}{" "}
              {siteFilter && (
                <>
                  <strong>{siteFilter}</strong> {s.addedAutomatically}.{" "}
                </>
              )}
              {s.clickToCheckGoogle}
            </p>
            <textarea className="intent-textarea" placeholder={s.enterSearchQueries} value={keywords} onChange={(e) => updateState("keywords", e.target.value)} style={{ minHeight: 330 }} />
            {queryCount > 0 && (
              <div className="queries-preview">
                {keywords.split("\n").filter((k) => k.trim()).map((query, idx) => {
                  const fullQuery = siteFilter ? `${siteFilter} ${query.trim()}` : query.trim();
                  return (
                    <div key={idx} className="query-row">
                      <code className="query-text">
                        {siteFilter && <span className="query-site-filter">{siteFilter} </span>}
                        {query}
                      </code>
                      <button className="btn-google-check" onClick={() => checkInGoogle(fullQuery)}>
                        🔗 {s.google}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="query-count">
              {queryCount} {queryCount === 1 ? s.activeQuery : s.activeQueries}
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="section-card">
            <button className={`advanced-toggle-btn${state.showAdvanced ? " open" : ""}`} onClick={() => updateState("showAdvanced", !state.showAdvanced)}>
              <span>⚙️ {s.advancedSettings}</span>
              <span className="optional-badge" style={{ fontSize: 10 }}>{s.optional}</span>
              <span className={`advanced-toggle-arrow${state.showAdvanced ? " open" : ""}`}>▼</span>
            </button>
            {state.showAdvanced && (
              <div className="advanced-content">
                <div>
                  <label className="advanced-field-label">🌐 {s.searchLanguage}</label>
                  <select className="form-input" value={state.advancedSettings?.searchLanguage || "en"} onChange={(e) => updateAdvancedSetting("searchLanguage", e.target.value)}>
                    <optgroup label={s.popular}>
                      {popularLanguages.map((l) => <option key={l.code} value={l.code}>{l.nativeName} ({l.name})</option>)}
                    </optgroup>
                    <optgroup label={s.allLanguages}>
                      {otherLanguages.map((l) => <option key={l.code} value={l.code}>{l.nativeName} ({l.name})</option>)}
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="advanced-field-label-row">📅 {s.scrapingPeriod}</label>
                  <div className="period-selector">
                    {PERIOD_OPTIONS.map((opt) => {
                      const isSelected = scrapingPeriods.includes(opt.value);
                      return (
                        <button key={opt.value} className={`period-chip${isSelected ? " selected" : ""}`} onClick={() => toggleScrapingPeriod(opt.value)}>
                          {opt.label}{isSelected && " ✓"}
                        </button>
                      );
                    })}
                  </div>
                  <div className="period-info">
                    {scrapingPeriods.includes("any") ? s.anyTimeSelected : `${scrapingPeriods.length} ${s.periodsSelected}`}
                  </div>
                </div>
                <div>
                  <label className="advanced-field-label">📄 {s.maxPagesPerQuery}</label>
                  <input className="form-input" type="text" value={state.advancedSettings?.maxPages || ""} onChange={(e) => updateAdvancedSetting("maxPages", e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {/* Schedule */}
          <div className="section-card">
            <h3>
              {s.schedule}
              <span className="required-badge" title={s.required}>*</span>
            </h3>
            <div className="schedule-grid">
              <button className={`schedule-btn${state.scheduleType === "once" ? " selected" : ""}`} onClick={() => updateState("scheduleType", "once")}>
                <span className="schedule-btn-icon">▶️</span>
                <span>{s.oneTime}</span>
                <span className="schedule-btn-desc">{s.runsOnce}</span>
              </button>
              <button className={`schedule-btn${state.scheduleType === "repeat" ? " selected" : ""}`} disabled onClick={() => updateState("scheduleType", "repeat")}>
                <span className="schedule-btn-icon">🔄</span>
                <span>{s.repeat}</span>
                <span className="schedule-btn-desc">{s.autoRuns}</span>
              </button>
            </div>
            {state.scheduleType === "repeat" && (
              <div className="schedule-repeat-options">
                <select className="form-input" value={state.repeatInterval || "daily"} onChange={(e) => updateState("repeatInterval", e.target.value)}>
                  <option value="daily">{s.daily}</option>
                  <option value="weekly">{s.weekly}</option>
                  <option value="monthly">{s.monthly}</option>
                </select>
                <input className="form-input" type="number" min="1" max="30" value={state.repeatCount || 1} onChange={(e) => updateState("repeatCount", e.target.value)} placeholder={s.every} />
              </div>
            )}
          </div>

          {includeFiltering && <FilteringSection selectedPlatform={state.selectedPlatform} />}
        </>
      )}

      {/* Credits & Actions */}
      {showOwnActions && (
        <>
          {!isSimpleProcessingMode && (
            <div className="credits-bar">
              <div className="credits-row">
                <span className="credits-label">{s.estimatedTokens}</span>
                <span className="credits-value">{credits.tokens}</span>
              </div>
            </div>
          )}

          <div className="form-actions-row">
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={
                saving ||
                !taskName?.trim() ||
                (isDnsMode ? 
                  (state.dnsFiles.length === 0 || dnsConditionsList.length === 0) :
                 isAdbMode ? 
                  (state.adbSourceType === "task" ? !state.adbTargetTaskId : !adbDirectUrls.trim()) :
                 isScreenshotMode ? !state.screenshotTargetTaskId :
                 isSpiderMode ? !state.spiderSourceTaskId || !keywords.trim() :
                 (!keywords.trim() || !state.selectedPlatform))
              }
            >
              {saving ? s.creating : (
                isDnsMode ? "📁 Create DNS Task" :
                isAdbMode ? (s.createAdbTask || "Create ADB Task") :
                isScreenshotMode ? (s.createScreenshotTask || "Create Screenshot Task") :
                isSpiderMode ? (s.createSpiderTask || "Create Spider Task") :
                s.createTask
              )}
            </button>
            <button className="btn-cancel" onClick={onCancel}>
              {s.cancel} 
            </button>
          </div>
        </>
      )}
    </>
  );
}